import React from 'react';

import {mergeSaveStatus, useSaveStatus, type LegacySaveStatus, type SaveStatusSnapshot} from '../lib/saveStatus.js';
import './HeaderPieces.css';
import Icon from './Icon.js';

// ============================================================================
// Header pieces for the phone header and the dark bar (Design Constitution N01,
// N02, N03, F02-B, Q02, Q17, Q18). New parts only: src/shared/Header.tsx adopts
// them once S2 releases it.
//
//   ProgramSwitcher   — 'Cattle ▾' + the section name, left of the phone bar
//                        (Q18); opens an N03 menu card of the programs the
//                        caller may open, each with its 7px program dot (C05).
//   SaveStatusChip    — the one global autosave indicator (F02-B): 'Saving…',
//                        'Saved', or 'Not Saved — Retry' with a danger dot
//                        (Q17: warnings on the bar are a translucent chip with a
//                        dot). Renders nothing while idle.
//   HeaderStatusStrip — the phone's thin strip under the bar holding weather,
//                        save status and the sync chip only while one is active
//                        (Q18).
// ============================================================================

export type ProgramChoice = {key: string; label: string; view: string};

type ProgramSwitcherProps = {
  current: ProgramChoice | null;
  sectionLabel?: string | null;
  programs: ProgramChoice[];
  onSelect: (program: ProgramChoice) => void;
};

export function ProgramSwitcher({current, sectionLabel, programs, onSelect}: ProgramSwitcherProps) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = React.useId();

  React.useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [open]);

  const focusItem = (i: number) => {
    const n = programs.length;
    if (n) itemRefs.current[((i % n) + n) % n]?.focus();
  };
  const openAt = (i: number) => {
    setOpen(true);
    requestAnimationFrame(() => focusItem(i));
  };
  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };
  const currentIndex = current ? programs.findIndex((p) => p.key === current.key) : -1;

  return (
    <div ref={rootRef} className="wcf-program-switcher" data-program-switcher="1">
      <button
        ref={triggerRef}
        type="button"
        className="wcf-program-switcher__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-program-switcher-trigger={current?.key ?? 'none'}
        onClick={() => (open ? close(false) : openAt(Math.max(0, currentIndex)))}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            openAt(Math.max(0, currentIndex));
          }
        }}
      >
        <span>{current ? current.label : 'Programs'}</span>
        <Icon name="chevronDown" size={16} />
      </button>
      {sectionLabel ? (
        <span className="wcf-program-switcher__section" data-program-switcher-section="1">
          {sectionLabel}
        </span>
      ) : null}
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Programs"
          className="wcf-header-menu-card"
          data-program-switcher-menu="1"
          onKeyDown={(e) => {
            const at = itemRefs.current.findIndex((el) => el === document.activeElement);
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              close(true);
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              focusItem(at + 1);
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              focusItem(at - 1);
            } else if (e.key === 'Home') {
              e.preventDefault();
              focusItem(0);
            } else if (e.key === 'End') {
              e.preventDefault();
              focusItem(programs.length - 1);
            } else if (e.key === 'Tab') {
              close(false);
            }
          }}
        >
          {programs.map((p, i) => {
            const selected = current?.key === p.key;
            return (
              <button
                key={p.key}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                aria-current={selected ? 'page' : undefined}
                className="wcf-header-menu-card__item"
                data-program-switcher-item={p.key}
                onClick={() => {
                  close(false);
                  onSelect(p);
                }}
              >
                <span
                  className="wcf-program-dot"
                  aria-hidden="true"
                  style={{background: `var(--c-${p.key}, var(--label))`}}
                />
                <span className="wcf-header-menu-card__label">{p.label}</span>
                {selected ? <Icon name="check" size={16} className="wcf-header-menu-card__check" /> : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** The chip text for a save state; null while idle (nothing shows). A failure
 * offers Retry only when there is something to retry. */
export function saveStatusText(state: SaveStatusSnapshot['state'], canRetry = true): string | null {
  if (state === 'saving') return 'Saving…';
  if (state === 'saved') return 'Saved';
  if (state === 'failed') return canRetry ? 'Not Saved — Retry' : 'Not Saved';
  return null;
}

export function SaveStatusChipView({status}: {status: SaveStatusSnapshot}) {
  const text = saveStatusText(status.state, !!status.retry);
  if (!text) return null;
  if (status.state === 'failed' && !status.retry) {
    return (
      <span className="wcf-bar-chip is-danger" data-save-status="failed" role="status" aria-live="polite">
        <span className="wcf-bar-chip__dot" aria-hidden="true" />
        {text}
      </span>
    );
  }
  if (status.state === 'failed') {
    return (
      <button
        type="button"
        className="wcf-bar-chip is-danger"
        data-save-status="failed"
        onClick={() => status.retry?.()}
        aria-live="polite"
      >
        <span className="wcf-bar-chip__dot" aria-hidden="true" />
        {text}
      </button>
    );
  }
  return (
    <span className="wcf-bar-chip" data-save-status={status.state} role="status" aria-live="polite">
      {text}
    </span>
  );
}

/** The live global indicator: src/lib/saveStatus merged with AuthContext's
 * older blob-save status, which Header passes in as `legacy`. */
export function SaveStatusChip({legacy}: {legacy?: LegacySaveStatus | null}) {
  return <SaveStatusChipView status={mergeSaveStatus(useSaveStatus(), legacy)} />;
}

export type StatusStripItem = {key: string; active: boolean; node: React.ReactNode};

/** Phone-only strip under the bar (Q18); renders nothing when no item is active. */
export function HeaderStatusStrip({items}: {items: StatusStripItem[]}) {
  const live = items.filter((i) => i.active);
  if (live.length === 0) return null;
  return (
    <div className="wcf-header-status-strip" data-header-status-strip="1">
      {live.map((i) => (
        <React.Fragment key={i.key}>{i.node}</React.Fragment>
      ))}
    </div>
  );
}
